import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowsLeftRight, ChartPie, CheckCircle, Coins, Files, Gauge, GridFour, MagnifyingGlass, SealCheck, Sliders, Stack, Table, Target, UploadSimple, Warning } from "@phosphor-icons/react";
import { monthLabel, useFpContext } from "@/lib/fp";
import { useAuth } from "@/context/AuthContext";

// setting up a brand new engagement is an Aikyam consultant action, so the tab is admin-only
const ADMIN_TAB = { to: "/panchatanthra/financial-engine/new-engagement",
                    label: "New engagement", icon: Sliders };

const TABS = [
  { to: "/panchatanthra/financial-engine", label: "Executive pack", icon: Gauge },
  { to: "/panchatanthra/financial-engine/overview", label: "Overview", icon: Stack },
  { to: "/panchatanthra/financial-engine/sources", label: "Statements", icon: UploadSimple },
  { to: "/panchatanthra/financial-engine/reconciliation-grids", label: "Reconciliation sheets", icon: Table },
  { to: "/panchatanthra/financial-engine/review", label: "Classification review", icon: CheckCircle },
  { to: "/panchatanthra/financial-engine/line-review", label: "Line-item approval", icon: SealCheck },
  { to: "/panchatanthra/financial-engine/explorer", label: "Explorer", icon: ChartPie },
  { to: "/panchatanthra/financial-engine/outputs", label: "Output pack", icon: Table },
  { to: "/panchatanthra/financial-engine/workbooks", label: "Workbooks & gap", icon: Files },
  { to: "/panchatanthra/financial-engine/trace", label: "Trace & exceptions", icon: MagnifyingGlass },
  { to: "/panchatanthra/financial-engine/level3", label: "Level 3 detail", icon: GridFour },
  { to: "/panchatanthra/financial-engine/mapping", label: "Mapping master", icon: ArrowsLeftRight },
  { to: "/panchatanthra/financial-engine/reconciliation", label: "Reconciliation", icon: CheckCircle },
  { to: "/panchatanthra/financial-engine/readiness", label: "Data readiness", icon: Stack },
  { to: "/panchatanthra/financial-engine/budgets", label: "Budgets", icon: Coins },
  { to: "/panchatanthra/financial-engine/parameters", label: "Parameters & goals", icon: Target },
  { to: "/panchatanthra/financial-engine/validation", label: "Score validation", icon: SealCheck },
  { to: "/panchatanthra/financial-engine/setup", label: "Configuration", icon: Sliders },
];

/** The simplified layout: six screens, each carrying the views that belong with it. Nothing is
 *  removed — a grouped view appears as a second row and every original link still works. */
const GROUPS = [
  { to: "/panchatanthra/financial-engine", label: "Executive pack", icon: Gauge,
    also: ["/panchatanthra/financial-engine/overview",
           "/panchatanthra/financial-engine/readiness"] },
  { to: "/panchatanthra/financial-engine/sources", label: "Documents", icon: UploadSimple,
    also: ["/panchatanthra/financial-engine/workbooks"] },
  { to: "/panchatanthra/financial-engine/reconciliation-grids", label: "Review & approve", icon: Table,
    also: ["/panchatanthra/financial-engine/review",
           "/panchatanthra/financial-engine/mapping",
           "/panchatanthra/financial-engine/line-review"] },
  { to: "/panchatanthra/financial-engine/outputs", label: "Financial model", icon: Table,
    also: ["/panchatanthra/financial-engine/level3",
           "/panchatanthra/financial-engine/reconciliation",
           "/panchatanthra/financial-engine/trace"] },
  { to: "/panchatanthra/financial-engine/explorer", label: "Explorer", icon: ChartPie },
  { to: "/panchatanthra/financial-engine/budgets", label: "Budgets & goals", icon: Coins,
    also: ["/panchatanthra/financial-engine/parameters",
           "/panchatanthra/financial-engine/validation"] },
  { to: "/panchatanthra/financial-engine/setup", label: "Configuration", icon: Sliders },
];

const LABEL_OF = Object.fromEntries(TABS.map((t) => [t.to, t.label]));
const ICON_OF = Object.fromEntries(TABS.map((t) => [t.to, t.icon]));

export const PeriodBar = () => {
  const { ctx, period, basis, setPeriod, setBasis } = useFpContext();
  const periods = ctx?.periods || [];
  const needsBuild = (ctx?.periods_needing_build || []).includes(period);
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="fp-period-bar">
      <label className="flex items-center gap-2">
        <span className="label-caps">Month</span>
        <select value={period} onChange={(e) => setPeriod(e.target.value)}
                data-testid="fp-period-select"
                className="rounded-full border border-border bg-card px-4 py-2 text-xs text-navy outline-none focus:border-terracotta">
          {!periods.length && <option value="">No statements yet</option>}
          {periods.map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
        </select>
      </label>
      <div className="flex overflow-hidden rounded-full border border-border" data-testid="fp-basis-toggle">
        {[["month", "This month"], ["ytd", "Cumulative (YTD)"]].map(([key, label]) => (
          <button key={key} onClick={() => setBasis(key)} data-testid={`fp-basis-${key}`}
                  className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors ${
                    basis === key ? "bg-terracotta text-white" : "bg-card text-navy hover:bg-muted"}`}>
            {label}
          </button>
        ))}
      </div>
      {needsBuild && (
        <Link to="/panchatanthra/financial-engine/workbooks"
              data-testid="fp-needs-build-warning"
              className="flex items-start gap-2 rounded-2xl border border-warning/60 bg-warning/10 px-4 py-2 text-[11px] leading-snug text-[hsl(30_88%_26%)] transition-colors hover:border-warning">
          <Warning size={14} className="mt-0.5 shrink-0" />
          <span>
            <strong>{monthLabel(period)} is not built yet.</strong> Files were received, but the
            month's workbook package has not been loaded — the figures below are incomplete.
            Attach this month's workbooks and press “Load into the engine”.
          </span>
        </Link>
      )}
    </div>
  );
};

export const FpShell = ({ title, description, children, actions, showPeriod = true }) => {
  const { pathname } = useLocation();
  const { ctx, period, basis } = useFpContext();
  const { isPlatformAdmin } = useAuth();
  return (
    <div className="space-y-6" data-testid="fp-shell">
      <Link to="/frameworks/panchatanthra/elements/FIN" data-testid="fp-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-navy hover:text-terracotta">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-navy text-white"><ArrowLeft size={13} /></span>
        Back to Financial Performance
      </Link>

      <div className="panel overflow-hidden">
        <div className="brand-bar h-1.5 w-full" />
        <div className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div className="flex items-start gap-5">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-terracotta text-white">
              <Table size={26} />
            </div>
            <div>
              <p className="label-caps">Panchatanthra · Financial Performance · Level 3 engine</p>
              <h1 className="font-display text-3xl tracking-tight text-navy">{title}</h1>
              {description && <p className="mt-1 max-w-3xl text-sm text-foreground/75">{description}</p>}
              {ctx?.fiscal_year_of_latest && (
                <p className="mt-2 text-[11px] text-muted-foreground" data-testid="fp-fy-note">
                  Financial year 1 April – 31 March · {ctx.fiscal_year_of_latest} ·
                  {" "}{basis === "ytd" ? "cumulative to" : "month of"} {period ? monthLabel(period) : "—"}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-start gap-3 md:items-end">
            {showPeriod && <PeriodBar />}
            {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-border px-6 py-4 md:px-8">
          {[...(ctx?.simplified_ui ? GROUPS : TABS),
            ...(isPlatformAdmin ? [ADMIN_TAB] : [])].map((t) => {
            const active = pathname === t.to || (t.also || []).includes(pathname);
            const suffix = period ? `?period=${period}&basis=${basis}` : "";
            return (
              <Link key={t.to} to={`${t.to}${suffix}`}
                    data-testid={`fp-tab-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] transition-colors ${
                      active ? "bg-terracotta text-white" : "border border-border text-navy hover:border-terracotta/50"}`}>
                <t.icon size={14} /> {t.label}
              </Link>
            );
          })}
        </div>
        {ctx?.simplified_ui && (() => {
          const group = GROUPS.find((g) => pathname === g.to || (g.also || []).includes(pathname));
          if (!group?.also?.length) return null;
          const suffix = period ? `?period=${period}&basis=${basis}` : "";
          return (
            <div className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/30 px-6 py-3 md:px-8"
                 data-testid="fp-subtabs">
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                Within {group.label}
              </span>
              {[group.to, ...group.also].map((to) => {
                const Icon = ICON_OF[to] || Table;
                return (
                  <Link key={to} to={`${to}${suffix}`}
                        data-testid={`fp-subtab-${(LABEL_OF[to] || to).toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.12em] transition-colors ${
                          pathname === to ? "bg-navy text-white" : "bg-card text-navy hover:text-terracotta"}`}>
                    <Icon size={12} /> {LABEL_OF[to] || to}
                  </Link>
                );
              })}
            </div>
          );
        })()}
      </div>

      {children}
    </div>
  );
};
